import { datadogRum } from '@datadog/browser-rum';
import { useState, useEffect, useCallback } from 'react';

export type ImageWithFallback<T> = {
  /**
   * Optional CSS classes to apply to the image
   */
  classes?: string;
  /**
   * The fallback image type to use in case the current image fails to load
   */
  fallbackImageType?: T;
  /**
   * The type of the image, used to uniquely identify it
   */
  imageType: T;
  /**
   * Should log when fallback image is set
   */
  shouldLogFallback?: boolean;
  /**
   * The URL of the image to load
   */
  url: string;
};

export type ImageMap<T extends string> = {
  [key in T]: ImageWithFallback<T>;
};

enum ImageError {
  LoadFailure = 'image_load_failure',
  EmptyUrl = 'empty_url'
}

/**
 * Custom hook to manage image loading with fallback URLs.
 * This hook attempts to load an image based on the provided `initialImageType`.
 * If the image fails to load, it will automatically switch to a defined `fallbackImage`
 * (if available) and continue the loading process. Additionally, it sends an action
 * to Datadog in case of an image load failure for tracking purposes.
 *
 * @template T - Represents the type of the image keys in the ImageMap
 *
 * @param {ImageMap<T>} images - A map of image types to their associated image details, including URL, fallbacks, and other metadata
 * @param {T} initialImageType - The initial image type to try loading first
 * @param {string} [imageLocation] - Optional location identifier used for tracking image load failures in Datadog
 * @param {() => void} [onFailure] - Optional callback invoked when an image fails to load and there is no fallback left to try
 *
 * @returns {ImageWithFallback<T> | undefined} - Returns the successfully loaded image details (type, URL, etc.), or `undefined` if no image could be loaded
 */
export default function useImageWithFallback<T extends string>(
  images?: ImageMap<T>,
  initialImageType?: T,
  imageLocation?: string,
  onFailure?: () => void
): ImageWithFallback<T> | undefined {
  const [currentImage, setCurrentImage] = useState<
    ImageWithFallback<T> | undefined
  >();
  const [loadedImage, setLoadedImage] = useState<
    ImageWithFallback<T> | undefined
  >();

  const handleImageError = useCallback(
    (imageError: ImageError, image: ImageWithFallback<T>) => {
      if (image.shouldLogFallback) {
        datadogRum.addAction(imageError, {
          imageType: image.imageType,
          location: imageLocation,
          url: image.url
        });
      }

      const fallbackImageType = image.fallbackImageType;
      if (fallbackImageType && images?.[fallbackImageType]) {
        setCurrentImage(images[fallbackImageType]);
      } else {
        setLoadedImage(undefined);
        onFailure?.();
      }
    },
    [imageLocation, images, onFailure]
  );

  // Reset current image when image map changes
  useEffect(() => {
    if (initialImageType && images?.[initialImageType]) {
      setCurrentImage(images[initialImageType]);
    } else {
      setLoadedImage(undefined);
    }
  }, [images, initialImageType]);

  useEffect(() => {
    if (!currentImage) return;

    // Fallback if url is empty
    if (!currentImage.url) {
      handleImageError(ImageError.EmptyUrl, currentImage);

      return;
    }

    // Load the current image and handle fallback on failure
    const img = new Image();
    const handleLoad = () => setLoadedImage(currentImage);
    const handleError = () =>
      handleImageError(ImageError.LoadFailure, currentImage);

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    img.src = currentImage.url; // Set src after listeners to ensure they catch all events

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
      img.src = ''; // Cancel in-flight requests
    };
  }, [currentImage, handleImageError]);

  return loadedImage;
}
